import { useMutation, useQueryClient } from "@tanstack/react-query";
import { t } from "i18next";

import { mutateData } from "@/core/api/api-methods";
import {
  apiErrorHandler,
  getBackendErrorMessage,
  getHttpStatus,
} from "@/core/api/api-error-handler";
import {
  invalidateAfterMutation,
  kycCaseCacheKeys,
} from "@/core/api/invalidate-after-mutation";
import { useSessionStore } from "@/core/stores/session.store";
import { isValidKycMimeType } from "@/core/utils/func/input-normalization";
import { logger } from "@/core/utils/func/logger";
import { kycEndpoints } from "@/features/kyc/api/endpoints";
import type {
  ConfirmKycDocumentRequestDto,
  KycDocumentDto,
  PresignDocumentResponseDto,
} from "@/features/kyc/api/dto";
import type { KycDocumentType, UploadedVia } from "@/features/shared/api/enums";

export interface UploadKycDocumentParams {
  kycCaseId: string;
  file: File;
  documentType: KycDocumentType;
  uploadedVia?: UploadedVia;
  /**
   * Repli annexe C / D : si le presign est indisponible (503), l'opérateur
   * dépose la pièce par un autre moyen puis confirme avec bucket/clé.
   */
  storageOverride?: { storageBucket: string; storageKey: string };
}

/** Empreinte SHA-256 hexadécimale du fichier (exigée par `confirm`, §6.2). */
const sha256Hex = async (file: File): Promise<string> => {
  const buffer = await file.arrayBuffer();
  const digest = await crypto.subtle.digest("SHA-256", buffer);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
};

/**
 * Orchestre le flux complet d'upload d'une pièce justificative (guide §6.2) :
 * 1. `presign` → URL présignée PUT (le binaire ne traverse jamais l'API) ;
 * 2. PUT direct du fichier vers `uploadUrl` (fetch natif, Content-Type = type
 *    MIME déclaré au presign) ;
 * 3. SHA-256 du fichier via `crypto.subtle.digest` ;
 * 4. `confirm` avec l'empreinte + `uploadedByKeycloakId` (claim `sub` de la
 *    session Keycloak) + `uploadedVia`.
 * Le dossier doit encore être en DRAFT côté serveur (sinon erreur métier).
 */
export const useUploadKycDocument = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["kyc", "kyc-documents", "upload"],
    mutationFn: async ({
      kycCaseId,
      file,
      documentType,
      uploadedVia,
      storageOverride,
    }: UploadKycDocumentParams): Promise<KycDocumentDto> => {
      const uploadedByKeycloakId = useSessionStore.getState().userInfo?.sub;
      if (typeof uploadedByKeycloakId !== "string" || !uploadedByKeycloakId) {
        throw new Error(t("kyc.documents.errors.sessionMissing"));
      }

      const mimeType =
        file.type === "image/jpg" ? "image/jpeg" : file.type || "";
      if (!isValidKycMimeType(mimeType)) {
        throw new Error(t("kyc.documents.errors.invalidMime"));
      }
      let storageBucket: string;
      let storageKey: string;

      if (storageOverride) {
        storageBucket = storageOverride.storageBucket;
        storageKey = storageOverride.storageKey;
      } else {
        try {
          const presign = (await mutateData({
            url: kycEndpoints.kycDocuments.presign(kycCaseId),
            reqBody: { documentType, mimeType },
            method: "POST",
          })) as PresignDocumentResponseDto;

          const uploadResponse = await fetch(presign.uploadUrl, {
            method: "PUT",
            headers: { "Content-Type": mimeType },
            body: file,
          });
          if (!uploadResponse.ok) {
            throw new Error(
              `Storage upload failed: ${uploadResponse.status} ${uploadResponse.statusText}`
            );
          }
          storageBucket = presign.storageBucket;
          storageKey = presign.storageKey;
        } catch (error) {
          if (getHttpStatus(error) === 503) {
            throw new Error("PRESIGN_UNAVAILABLE");
          }
          throw error;
        }
      }

      const checksumSha256 = await sha256Hex(file);

      const confirmBody: ConfirmKycDocumentRequestDto = {
        documentType,
        storageBucket,
        storageKey,
        checksumSha256,
        mimeType,
        uploadedByKeycloakId,
        uploadedVia,
      };
      return (await mutateData({
        url: kycEndpoints.kycDocuments.confirm(kycCaseId),
        reqBody: confirmBody as unknown as Record<string, unknown>,
        method: "POST",
      })) as KycDocumentDto;
    },
    onSuccess: (_data, { kycCaseId }) => {
      return invalidateAfterMutation(queryClient, kycCaseCacheKeys(kycCaseId));
    },
    onError: (error) => {
      const message = getBackendErrorMessage(error) ?? apiErrorHandler(error);
      logger.error(
        `useUploadKycDocument failed: ${message}`,
        error instanceof Error ? error : undefined,
        { prefix: "kyc" }
      );
    },
  });
};
